'use client';
import React, { useState } from 'react';
import dynamic from 'next/dynamic';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';

const LineChart = dynamic(() => import('recharts').then(m => m.LineChart), { ssr: false });
const Line = dynamic(() => import('recharts').then(m => m.Line), { ssr: false });
const XAxis = dynamic(() => import('recharts').then(m => m.XAxis), { ssr: false });
const YAxis = dynamic(() => import('recharts').then(m => m.YAxis), { ssr: false });
const Tooltip = dynamic(() => import('recharts').then(m => m.Tooltip), { ssr: false });
const ResponsiveContainer = dynamic(() => import('recharts').then(m => m.ResponsiveContainer), { ssr: false });

const dailyPriceData = [
  { date: '01 Aoû', value: 74 }, { date: '03 Aoû', value: 76 }, { date: '05 Aoû', value: 75 },
  { date: '07 Aoû', value: 79 }, { date: '09 Aoû', value: 81 }, { date: '11 Aoû', value: 80 },
  { date: '13 Aoû', value: 83 }, { date: '15 Aoû', value: 85 }, { date: '17 Aoû', value: 84 },
  { date: '19 Aoû', value: 87 }, { date: '21 Aoû', value: 89 }, { date: '23 Aoû', value: 88 },
  { date: '25 Aoû', value: 91 }, { date: '27 Aoû', value: 90 }, { date: '29 Aoû', value: 93 },
];

interface ScoreCardProps {
  label: string;
  value: number | string;
  max?: number;
  color: string;
  icon: string;
  description: string;
  confidence: string;
  methodology: string;
}

function ScoreCard({ label, value, max = 100, color, icon, description, confidence, methodology }: ScoreCardProps) {
  const [showMeta, setShowMeta] = useState(false);
  const numVal = typeof value === 'number' ? value : parseInt(value as string);
  const pct = Math.min(100, (numVal / max) * 100);

  return (
    <div className="bg-card border border-border rounded-xl p-5 flex flex-col gap-3">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${color}`}>
            <Icon name={icon as Parameters<typeof Icon>[0]['name']} size={16} className="text-white" />
          </div>
          <span className="text-sm font-600 text-foreground">{label}</span>
        </div>
        <button onClick={() => setShowMeta(!showMeta)} className="text-muted-foreground hover:text-foreground transition-colors">
          <Icon name="InformationCircleIcon" size={16} />
        </button>
      </div>
      <div className="flex items-end gap-1">
        <span className="text-metric-lg font-mono text-foreground">{value}</span>
        <span className="text-sm text-muted-foreground mb-1">/{max}</span>
      </div>
      <div className="w-full bg-muted rounded-full h-1.5">
        <div className="h-1.5 rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: 'var(--primary)' }} />
      </div>
      <p className="text-xs text-muted-foreground">{description}</p>
      {showMeta && (
        <div className="bg-muted/50 rounded-lg p-3 space-y-1 border border-border/50">
          <p className="text-[11px] text-muted-foreground"><span className="font-600 text-foreground">Confiance :</span> {confidence}</p>
          <p className="text-[11px] text-muted-foreground"><span className="font-600 text-foreground">Méthodologie :</span> {methodology}</p>
          <p className="text-[11px] text-warning">⚠ Score indicatif — pas une recommandation financière</p>
        </div>
      )}
    </div>
  );
}

const scores: ScoreCardProps[] = [
  { label: 'ISX Score Global', value: 87, max: 100, color: 'bg-primary', icon: 'StarIcon', description: 'Score synthétique ISX — comparé à la cohorte Beauty/DZ/100K–500K', confidence: 'Élevée (données 90j)', methodology: 'v1.2 — Pondération engagement 35%, croissance 25%, qualité audience 25%, régularité 15%' },
  { label: 'Influencer Score', value: 91, max: 100, color: 'bg-blue-500', icon: 'UserIcon', description: 'Performance et engagement vs créateurs comparables', confidence: 'Élevée', methodology: 'v1.2 — Engagement rate, reach, views normalisés par cohorte' },
  { label: 'Brand Score', value: 88, max: 100, color: 'bg-teal-500', icon: 'BuildingOffice2Icon', description: 'Adéquation avec les collaborations commerciales', confidence: 'Moyenne', methodology: 'v1.1 — Brand safety signals, historique collaborations, audience démographique' },
  { label: 'Risk Score', value: 12, max: 100, color: 'bg-green-600', icon: 'ShieldCheckIcon', description: 'Risque faible — score inversé (plus bas = meilleur)', confidence: 'Élevée', methodology: 'v1.2 — Signaux négatifs pondérés : inauthenticité, controverses, incohérences' },
  { label: 'Audience Quality', value: 91, max: 100, color: 'bg-violet-500', icon: 'UsersIcon', description: 'Authenticité et qualité de l\'audience détectée', confidence: 'Élevée', methodology: 'v1.2 — Ratio followers/engagement, détection bots, cohérence démographique' },
  { label: 'Market Value', value: '4.2M', max: 10, color: 'bg-amber-500', icon: 'CurrencyDollarIcon', description: 'Valeur commerciale estimée ISX (indice DZD × 1000) — indicatif uniquement', confidence: 'Faible–Moyenne', methodology: 'v0.9 — Modèle expérimental. Ne constitue pas un prix de marché réel.' },
  { label: 'Campaign Prediction', value: 76, max: 100, color: 'bg-orange-500', icon: 'ChartBarIcon', description: 'Performance estimée d\'une future campagne', confidence: 'Faible (données limitées)', methodology: 'v0.8 — Modèle préliminaire. Fiabilité augmente avec l\'historique.' },
  { label: 'Growth Forecast', value: 68, max: 100, color: 'bg-pink-500', icon: 'ArrowTrendingUpIcon', description: 'Perspectives de croissance sur 90 jours', confidence: 'Faible–Moyenne', methodology: 'v0.8 — Tendance historique + signaux de momentum. Prévision non garantie.' },
];

export default function ISXScoringPage() {
  const [period, setPeriod] = useState<'30d' | '90d' | '1y'>('30d');

  return (
    <AppLayout currentPath="/isx-scoring">
      <div className="space-y-8 animate-fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-700 uppercase tracking-widest text-primary bg-primary/10 px-2 py-0.5 rounded-full">ISX v1.2</span>
              <span className="text-xs text-muted-foreground">Dernière mise à jour : 19 Aoû 2026</span>
            </div>
            <h1 className="text-2xl font-700 text-foreground">ISX Scoring System</h1>
            <p className="text-sm text-muted-foreground mt-1">Notation indépendante · Cohorte : Beauty / DZ / 100K–500K</p>
          </div>
          <div className="flex items-center gap-2">
            <Link href="/score-dispute" className="flex items-center gap-1.5 px-3 py-2 bg-muted hover:bg-muted/80 border border-border rounded-lg text-sm font-600 text-foreground transition-colors">
              <Icon name="FlagIcon" size={14} />
              Contester un score
            </Link>
            <button className="flex items-center gap-1.5 px-3 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg text-sm font-600 transition-colors">
              <Icon name="ArrowDownTrayIcon" size={14} />
              Export API
            </button>
          </div>
        </div>

        {/* API Disclaimer */}
        <div className="bg-warning/5 border border-warning/20 rounded-xl p-4 flex gap-3">
          <Icon name="ExclamationTriangleIcon" size={18} className="text-warning flex-shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="text-sm font-600 text-foreground">Avertissement ISX — Données indicatives</p>
            <p className="text-xs text-muted-foreground">Les scores ISX sont des indicateurs analytiques indépendants. Ils ne constituent pas des conseils financiers, des recommandations d'investissement, des titres financiers, ni des certifications officielles. ISX n'est pas une bourse financière. Méthodologie v1.2 — Niveau de confiance variable selon la disponibilité des données. Toute décision commerciale reste sous la responsabilité exclusive de l'utilisateur.</p>
          </div>
        </div>

        {/* Score Grid */}
        <div>
          <h2 className="text-base font-700 text-foreground mb-4">Les 8 indicateurs ISX</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {scores.map((s) => (
              <ScoreCard key={s.label} {...s} />
            ))}
          </div>
        </div>

        {/* Daily Price History */}
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h2 className="text-base font-700 text-foreground">Daily Price History</h2>
              <p className="text-xs text-muted-foreground mt-0.5">Évolution de la Market Value ISX — indicateur interne uniquement</p>
            </div>
            <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
              {(['30d', '90d', '1y'] as const).map(p => (
                <button key={p} onClick={() => setPeriod(p)} className={`px-3 py-1 rounded-md text-xs font-600 transition-all ${period === p ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>{p}</button>
              ))}
            </div>
          </div>
          <div className="bg-warning/5 border border-warning/20 rounded-lg px-3 py-2 mb-4 flex items-center gap-2">
            <Icon name="InformationCircleIcon" size={14} className="text-warning flex-shrink-0" />
            <p className="text-[11px] text-muted-foreground">Ce graphique représente un indice ISX interne. Il ne simule pas un cours boursier et ne doit pas être interprété comme tel.</p>
          </div>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={dailyPriceData}>
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }} axisLine={false} tickLine={false} />
                <YAxis domain={[60, 100]} tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }} />
                <Line type="monotone" dataKey="value" stroke="var(--primary)" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Profile Status */}
        <div className="bg-card border border-border rounded-xl p-5">
          <h2 className="text-base font-700 text-foreground mb-4">Statut du profil ISX</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
            {[
              { label: 'Statut', value: 'Verified & Current', color: 'text-positive' },
              { label: 'Données', value: 'Complètes', color: 'text-positive' },
              { label: 'Connexion', value: 'Active', color: 'text-positive' },
              { label: 'Dernière sync', value: '19 Aoû 2026', color: 'text-foreground' },
              { label: 'Méthodologie', value: 'v1.2', color: 'text-foreground' },
              { label: 'Prochaine MAJ', value: 'Dans 7j', color: 'text-muted-foreground' },
            ].map(item => (
              <div key={item.label} className="bg-muted/40 rounded-lg p-3">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-600 mb-1">{item.label}</p>
                <p className={`text-xs font-700 ${item.color}`}>{item.value}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Legal Footer */}
        <div className="border-t border-border pt-4">
          <p className="text-[11px] text-muted-foreground/60 leading-relaxed">
            <strong className="text-muted-foreground">Avertissement légal ISX :</strong> Influencer Stock Exchange (ISX) est un fournisseur indépendant d'indices, de notation et d'analyses pour l'économie des créateurs. ISX n'est pas une bourse financière, ne vend pas d'actions, ne crée pas de titres financiers, ne propose pas de produits dérivés et n'est pas un courtier. Les scores, indices et valeurs produits par ISX sont des indicateurs analytiques internes à usage informatif uniquement. Ils ne constituent pas des conseils financiers, fiscaux ou juridiques. Toute décision commerciale ou financière reste sous l'entière responsabilité de l'utilisateur. © 2026 ISX — Institut Reversal Group.
          </p>
        </div>
      </div>
    </AppLayout>
  );
}
